import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import { useEffect } from 'react'

// All 4 brand colors — mirror repeat creates a seamless back-and-forth cycle
const GRADIENT_LOOP = 'linear-gradient(135deg, #FF9D76 0%, #C176FF 33%, #76F1FF 67%, #FFE176 100%)'

interface AnimatedGradientBackgroundProps {
  /** Opacity of the gradient layer — keep low for a subtle ambient effect */
  opacity?: number
  /** Seconds for one half-cycle (it mirrors, so full round-trip is 2×) */
  duration?: number
}

/**
 * Fixed full-viewport gradient background that auto-plays on mount.
 * Drop this as the first child of any page that needs the ambient color effect.
 */
export function AnimatedGradientBackground({
  opacity = 0.12,
  duration = 12,
}: AnimatedGradientBackgroundProps) {
  const bgPos = useMotionValue(0)
  const backgroundPosition = useTransform(bgPos, v => `${v}% ${v}%`)

  useEffect(() => {
    const controls = animate(bgPos, [0, 100], {
      duration,
      repeat: Infinity,
      repeatType: 'mirror',
      ease: 'easeInOut',
    })
    return () => controls.stop()
  }, [bgPos, duration])

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0"
      style={{
        background: GRADIENT_LOOP,
        backgroundSize: '400% 400%',
        backgroundPosition,
        opacity,
      }}
    />
  )
}
